/* eslint-disable one-var */
/* eslint-disable func-names */
/* eslint-disable no-console */

/**
 * @fileOverview Marquee
 * @author Selectra Front End Team
 * @version 1.0.0
 * @requires Polyfill:classlist 'classList'
 * @requires Polyfill:array_from 'array-from'
 * @requires Polyfill:intersection-observer 'intersection-observer'
 */


/**
 * @function initMarquee
 * @description Initializes a marquee
 * @param {Object} opts - Options object.
 * @param {String} opts.marqueeId - Target marquee ID attribute
 * @param {String} [opts.direction=right] - Scroll direction 'left' || 'right'
 * @returns {null|Boolean} null in case of failure, true otherwise
 */
export default function initMarquee( opts ) {

  const
    options = {
      marqueeId: opts.marqueeId,
      direction: opts.direction || 'right',
    },
    marquee = document.getElementById( options.marqueeId );

  if ( !marquee ) {
    console.error( 'Selector did not match anything, Bugging Out!' );
    return null;
  }

  marquee.addEventListener( 'marquee_images_loaded', buildMarquee );

  let lazyImages =
    marquee.querySelectorAll( '[data-lazyMarqueeSrc]' );

  // Manage lazy load images
  if ( lazyImages.length ) {

    const observer = new IntersectionObserver( loadListLazyImages );
    observer.observe( marquee );

  } else {
    checkImagesAreLoaded(
      Array.from( marquee.querySelectorAll( '.marquee__img' ))
    );
  }


  /**
   * @callback initMarquee~loadListLazyImages
   * @description Sets the 'src' attribute on each image of the marquee that has
   *     a 'lazyMarqueeSrc' data attribute.
   * @param {Array} entries - Array of IntersectionObserverEntry objects.
   * @param {Object} observer - The IntersectionObserver object.
   */
  function loadListLazyImages( entries, observer ) {
    entries.forEach( entry => {
      if ( entry.isIntersecting ) {
        const images =
          Array.from( entry.target.querySelectorAll( '[data-lazyMarqueeSrc]' ));
        images.forEach( image => {
          image.src = image.dataset.lazymarqueesrc;
        });
        observer.disconnect();
        checkImagesAreLoaded( images );
      }
    });
  }


  /**
   * @function checkImagesAreLoaded
   * @description Checks if all image elements on the array finished loading.
   * @param {Array} images
   * @fires #event:marquee_images_loaded
   */
  function checkImagesAreLoaded( images ) {

    let
      currentTest = 0,
      listImagesAreLoaded = false;

    const
      maxTests = 10;

    ( function testImages( images ) {

      if ( currentTest++ === maxTests ) {
        console.error( 'Images did not load in time. Bugging Out!' );
        return null;
      }

      for ( let i = 0; i < images.length; ++i ) {
        listImagesAreLoaded = images[ i ].complete;
        if ( !listImagesAreLoaded ) {
          break;
        }
      }

      if ( listImagesAreLoaded ) {
        marquee.dispatchEvent( new CustomEvent( 'marquee_images_loaded' ));
      } else {
        setTimeout( testImages, 100, images );
      }

    }( images ));

  }


  /**
   * @callback initMarquee~buildMarquee
   * @description Performs DOM manipulations (cloning, positioning) and starts
   *     the scroll animation.
   */
  function buildMarquee() {

    marquee.removeEventListener( 'marquee_images_loaded', buildMarquee );

    const
      images = Array.from( marquee.getElementsByClassName( 'marquee__img' ));

    // Use the size of the smallest image as marquee height
    ( function setMarqueeHeight() {
      let marqueeHeight = Infinity;

      images.forEach( image => {
        marqueeHeight =
          marqueeHeight > image.offsetHeight ?
            image.offsetHeight : marqueeHeight;
      });

      marqueeHeight = parseInt( marqueeHeight );
      if ( !isNaN( marqueeHeight )) {
        marquee.style.height = `${ marqueeHeight }px`;
      }
    }());

    let
      list = marquee.querySelector( '.marquee__list' ),
      listInitialOffset = 0,
      listScroll = 0,
      cloneSets = Math.ceil( marquee.offsetWidth / list.offsetWidth );

    const
      listInitialWidth = list.offsetWidth,
      margin =
        list.children[ 1 ] ?
          parseFloat(
            window
              .getComputedStyle( list.children[ 1 ])
              .getPropertyValue( 'margin-left' )
          ) : 16;

    // Calculate initial list position
    if ( options.direction === 'right' ) {
      listInitialOffset = -listInitialWidth - margin;
    }
    listScroll = listInitialOffset;

    ( function generateClones() {
      let newList = list.cloneNode( true );
      for ( let set = 0; set < cloneSets; ++set ) {
        for ( let child = 0; child < list.children.length; child++ ) {
          const clone = list.children[ child ].cloneNode( true );
          clone.style.marginLeft = margin;
          newList.appendChild( clone );
        }
      }
      newList.style.transform = `translate3d( ${ listScroll }px, 0, 0 )`;
      list.parentElement.replaceChild( newList, list );
      list = newList;
      newList = null;
    }());

    // Show the list
    marquee.classList.add( 'js-is--ready' );

    // It's best for performance to keep left/right logic
    // outside of play function...
    switch ( options.direction ) {
    case 'left':
      playLeft();
      break;
    case 'right':
      playRight();
      break;
    }

    // Everything went well, phew!
    // See ya, captain!
    return true;


    /**
     * @function playLeft
     * @description Update marquee's list element position (Scroll left)
     */
    function playLeft() {

      // Update position
      listScroll =
        --listScroll === -( listInitialWidth + margin ) ?
          0 : listScroll;

      // Render
      list.style.transform = `translate3d(${ listScroll }px, 0, 0)`;

      // Loop
      requestAnimationFrame( playLeft );
    }


    /**
     * @function playRight
     * @description Update marquee's list element position (Scroll right)
     */
    function playRight() {

      // Update position
      listScroll =
        ++listScroll === 0 ?
          listInitialOffset : listScroll;

      // Render
      list.style.transform = `translate3d(${ listScroll }px, 0, 0)`;

      // Loop
      requestAnimationFrame( playRight );
    }

  }
}
